import React, { useState } from 'react';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, MarkerType, useStore } from 'reactflow';
import { IconButton, Tooltip, TextField } from '@mui/material';
import { Delete as DeleteIcon, Close as CloseIcon, } from '@mui/icons-material';
import NodeLibrary from './NodeLibrary';
import { borderRadius } from '../../../../../node_modules/@mui/system/index';

const CustomEdge = ({ id, source, target, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style = {}, markerEnd, data, selected, sourceHandle: sourceHandleProp, type }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [edgePath, labelX, labelY] = getSmoothStepPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  const [isActive, setIsActive] = useState(false);
  const [onPopupToggle, setOnPopupToggle] = useState(false);

  // Get the full edge data from React Flow store to access sourceHandle
  const edge = useStore((state) => state.edges.find((e) => e.id === id));
  const sourceHandle = edge?.sourceHandle || sourceHandleProp;



  const onDelete = (event) => {
    event.stopPropagation();
    if (data?.onDelete) data.onDelete(id);
  }

  const handleToggle = () => {
    console.log('[CustomEdge] Plus icon clicked on edge:', id, 'source:', source, 'target:', target);
    setIsActive((prev) => !prev); // toggle true/false
    if (onPopupToggle) {
      onPopupToggle(next); // 🚀 parent updates disableCanvas
    }
  };
  
  // Get edge label based on sourceHandle for decision nodes
  const getEdgeLabel = () => {
    console.log('[CustomEdge] getEdgeLabel - Edge:', id, 'Source:', source, 'SourceHandle:', sourceHandle, 'Type:', type, 'DataType:', data?.type, 'DataEdgeType:', data?.edgeType, 'SourceNodeType:', data?.sourceNodeType);

    // Simple decision node detection - only proceed if we have clear indicators
    const isFromDecisionNode =
      type === 'conditional' ||
      data?.edgeType === 'conditional' || 
      data?.type === 'conditional' ||
      data?.sourceNodeType === 'decision';
    
    console.log('[CustomEdge] Decision node detection:', {
      isFromDecisionNode,
      sourceHandle,
      sourceHandleType: typeof sourceHandle
    });
    
    // Only show labels for decision nodes
    if (!isFromDecisionNode) {
      console.log('[CustomEdge] Not a decision node edge, no label');
      return null;
    }
    
    // Check sourceHandle values directly - handle all possible formats
    const sourceHandleStr = String(sourceHandle).toLowerCase();
    
    if (sourceHandle === 'true' || sourceHandle === true || sourceHandleStr === 'true') {
      console.log('[CustomEdge] Returning "True" label for sourceHandle:', sourceHandle, 'type:', typeof sourceHandle);
      return 'True';
    }
    
    if (sourceHandle === 'false' || sourceHandle === false || sourceHandleStr === 'false') {
      console.log('[CustomEdge] Returning "False" label for sourceHandle:', sourceHandle, 'type:', typeof sourceHandle);
      return 'False';
    }
    
    // If it's a decision node but sourceHandle is unclear, warn but don't show label
    console.warn(`[CustomEdge] Decision edge ${id} has unclear sourceHandle:`, sourceHandle, 'type:', typeof sourceHandle);
    return null;
  };

  return (
    <>
      <g onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} >
        {/* Invisible wider path for better hover detection */}
        <path
          d={edgePath}
          fill="none"
          strokeWidth={10}
          stroke="transparent"
          style={{ cursor: 'pointer' }}
        />

        {/* Visible edge */}
        <BaseEdge
          path={edgePath}
          markerEnd={markerEnd || { type: MarkerType.ArrowClosed, width: 10, height: 10, color: selected ? '#1976d2' : '#CAD6E4' }}
          style={{
            ...style,
            cursor: 'pointer',
            pointerEvents: 'none', // Let the parent g handle events
            transition: 'all 0.2s ease',
            strokeWidth: selected || isHovered ? 1 : 1,
            stroke: selected ? 'var(--primary-color)' : (isHovered ? '#CAD6E4' : '#CAD6E4'),
          }}
        />
      </g>
      
      {/* Edge label for decision nodes */}
      {(() => {
        const label = getEdgeLabel();
        return label ? (
          <EdgeLabelRenderer>
            <div
              style={{
                position: 'absolute',
                transform: `translate(-50%, -220%) translate(${labelX}px,${labelY - 10}px)`,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--sa-dark-surface)',
                borderRadius: '12px',
                padding: '2px 8px',
                border: '1px solid #CAD6E4',
                fontSize: '8px',
                fontWeight: '500',
                color: '#374151',
                zIndex: '5'
              }}
            >
              {label}
            </div>
          </EdgeLabelRenderer>
        ) : null;
      })()}

      {(isHovered || selected) && (
        <EdgeLabelRenderer>
          <div
            onMouseEnter={() => setIsHovered(true)}   // 👈 added
            onMouseLeave={() => setIsHovered(false)}  // 👈 added
            style={{
              position: 'absolute',
              transform: `translate(-9px, 10px) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2px',
              zIndex: '99'
            }}
            className="nodrag nopan"
          >
            <Tooltip title="Delete connection">
              <button
                size="small"
                className='delete-edge-btn'
                onClick={onDelete}
                style={{
                  backgroundColor: 'var(--sa-dark-surface)',
                  boxShadow: 1,
                  width: 15,
                  height: 15,
                  padding: '1px',
                  position: 'relative',
                  zIndex: '9',
                  opacity: '1',
                  lineHeight: '18px',
                  borderRadius: '50%',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.25 3.92578C12.2383 3.92578 12.2208 3.92578 12.2033 3.92578C9.11751 3.61661 6.03751 3.49995 2.98668 3.80911L1.79668 3.92578C1.55168 3.94911 1.33585 3.77411 1.31251 3.52911C1.28918 3.28411 1.46418 3.07411 1.70335 3.05078L2.89335 2.93411C5.99668 2.61911 9.14084 2.74161 12.2908 3.05078C12.53 3.07411 12.705 3.28995 12.6817 3.52911C12.6642 3.75661 12.4717 3.92578 12.25 3.92578Z" fill="#4B5563"></path><path d="M4.95832 3.33675C4.93499 3.33675 4.91165 3.33675 4.88249 3.33091C4.64915 3.29008 4.48582 3.06258 4.52665 2.82925L4.65499 2.06508C4.74832 1.50508 4.87665 0.729248 6.23582 0.729248H7.76415C9.12915 0.729248 9.25749 1.53425 9.34499 2.07091L9.47332 2.82925C9.51415 3.06841 9.35082 3.29591 9.11749 3.33091C8.87832 3.37175 8.65082 3.20841 8.61582 2.97508L8.48749 2.21675C8.40582 1.70925 8.38832 1.61008 7.76998 1.61008H6.24165C5.62332 1.61008 5.61165 1.69175 5.52415 2.21091L5.38999 2.96925C5.35499 3.18508 5.16832 3.33675 4.95832 3.33675Z" fill="#4B5563"></path><path d="M8.87252 13.2708H5.12752C3.09169 13.2708 3.01002 12.1449 2.94586 11.2349L2.56669 5.36075C2.54919 5.12159 2.73586 4.91159 2.97502 4.89409C3.22002 4.88242 3.42419 5.06326 3.44169 5.30242L3.82086 11.1766C3.88502 12.0633 3.90836 12.3958 5.12752 12.3958H8.87252C10.0975 12.3958 10.1209 12.0633 10.1792 11.1766L10.5584 5.30242C10.5759 5.06326 10.7859 4.88242 11.025 4.89409C11.2642 4.91159 11.4509 5.11576 11.4334 5.36075L11.0542 11.2349C10.99 12.1449 10.9084 13.2708 8.87252 13.2708Z" fill="#4B5563"></path><path d="M7.96832 10.0625H6.02582C5.78665 10.0625 5.58832 9.86417 5.58832 9.625C5.58832 9.38583 5.78665 9.1875 6.02582 9.1875H7.96832C8.20748 9.1875 8.40582 9.38583 8.40582 9.625C8.40582 9.86417 8.20748 10.0625 7.96832 10.0625Z" fill="#4B5563"></path><path d="M8.45835 7.72925H5.54169C5.30252 7.72925 5.10419 7.53091 5.10419 7.29175C5.10419 7.05258 5.30252 6.85425 5.54169 6.85425H8.45835C8.69752 6.85425 8.89585 7.05258 8.89585 7.29175C8.89585 7.53091 8.69752 7.72925 8.45835 7.72925Z" fill="#4B5563"></path></svg>
              </button>
            </Tooltip>
          </div>
        </EdgeLabelRenderer>
      )}

      {(source || target) && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '4',
              opacity: '1'
            }}
            onWheel={(e) => e.stopPropagation()}
            className="nodrag nopan"
          >
            <Tooltip title="Add Node" onWheel={(e) => e.stopPropagation()}>
              <button
                size="small"
                style={{
                  backgroundColor: 'var(--sa-dark-surface)',
                  boxShadow: 1,
                  width: 15,
                  height: 15,
                  padding: '1px',
                  position: 'relative',
                  zIndex: '9',
                  opacity: '1',
                  lineHeight: '18px',
                  borderRadius: '50%',
                }}
                className='add-node-btn'
                onClick={(e) => { e.stopPropagation(); handleToggle(); }}
              >
                <svg width="24px" height="24px" viewBox="0 0 32 32" id="i-plus" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentcolor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"><g id="SVGRepo_bgCarrier" strokeWidth="0" /><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" /><g id="SVGRepo_iconCarrier"> <path d="M16 2 L16 30 M2 16 L30 16" /> </g></svg>
              </button>
              {isActive && (
                <div
                  className="node-popup-edit style-two"
                  style={{ pointerEvents: 'auto', zIndex: 9999 }}
                  onClick={(e) => e.stopPropagation()}
                  onWheel={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <div className="node-popup-inner">
                    <div className='left-side-bar'>
                      <div className='title-area'>
                        <div className='title-top'>
                          <h4>Add Node</h4>
                          <p>Select a node to add to your workflow</p>
                          <IconButton size="small" onClick={() => setIsActive(false)}>
                            <CloseIcon />
                          </IconButton>
                        </div>
                      </div>
                      {data?.nodeTemplates && Object.keys(data.nodeTemplates).length > 0 ? (
                        <NodeLibrary
                          nodeTemplates={data.nodeTemplates}
                          onNodeSelect={(nodeType, customConfig) => {
                            if (data?.onAddNode) {
                              data.onAddNode(id, nodeType, customConfig);
                            } else {
                              console.error('[CustomEdge] onAddNode function not available in edge data');
                            }
                            setIsActive(false);
                          }}
                          readOnly={false}
                          searchable={true}
                          compact={true}
                        />
                      ) : (
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                          <p>Loading nodes...</p>
                          <p style={{ fontSize: '12px', color: '#666' }}>
                            {!data ? 'No edge data' : 'No node templates available'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Tooltip>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  )
}

export default CustomEdge;